import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Container, Grid2 as Grid, Menu, MenuItem, useMediaQuery, useTheme } from "@mui/material";
import {
    ArrowBack as ArrowBackIcon
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";

import {
    BasicLoader, BottomSearchAppBar,
    CriticalOrSiteSpecificHazardListItemReadOnly, FilledButton, GeneralChip,
    JobTitle,
    LabelledProfileImage, PlanListItem,
    PrestartHazardListItem,
    PrestartListItem,
    TextButton,
    Typography, useSnackbar
} from "@initia-digital/component-library";
import { AuthLibMSALTokenAcquisitionError, useMSALPayload } from "@initia-digital/auth-client-utils";

import { BasicAppBarPage } from "../../layouts/BasicAppBarPage";
import { useFullTeamList, useIsAdmin, useJobSearch } from "../../hooks";
import { fetchJobDetail, fetchOlapQuery, fetchPlanList, fetchPrestartList } from "../../api/query";
import { baseDefinedAndNotNullNarrowing, BasicLogger } from "@initia-digital/ts-utils";
import { compareHazardsByRiskLevel, comparePlanDates, comparePrestartDates, INITIATOR, OrgSystem, SALESFORCE_JOB_STAFF_CONFIGURATION } from "@initia-digital/data-service-mapping";

import { CONFIG } from "../../config/const"
import { EmergencyDirectiveBanner } from "../../legacy/EmergencyDirectiveBanner";
import { patchPlan, patchPrestart } from "../../api/mutate";
import { amber } from "@mui/material/colors";
import { IHazardMedia } from "@initia-digital/types/work-and-safety";
import { loadFileUrl } from "../../utils/files";

const { CDN_BASE_URL } = CONFIG;


const logger = new BasicLogger({ logLevel: import.meta.env.VITE_ENV_LOWER === 'prod' ? 0 : 2, logFormat: 'BROWSER' });


const JobDetail = () => {
    // --------------------------------------------
    const [prestartMenuAnchorEl, setPrestartMenuAnchorEl] = useState<null | HTMLElement>(null);

    const theme = useTheme();
    const isSmallerThanSm = useMediaQuery(theme.breakpoints.down('sm'));
    const { jobId } = useParams();
    const hasJobId = baseDefinedAndNotNullNarrowing(jobId);

    const navigate = useNavigate();

    const { queueSnackbar } = useSnackbar();

    const authDetail = useMSALPayload();
    const isAdmin = useIsAdmin(authDetail);

    const searchJob = useJobSearch(authDetail);

    // *******************************
    // QUERY AND MUTATE
    // *******************************
    const queryClient = useQueryClient();

    const { fullTeamListData } = useFullTeamList(authDetail);

    // -----------------------
    //    JOB DETAIL
    // -----------------------
    const { data: jobDetail, isLoading: isJobDetailLoading } = useQuery({
        queryKey: ['job', jobId],
        queryFn: async () => {
            const result = await fetchJobDetail(authDetail, jobId!)
            return result;
        },
        enabled: hasJobId,
        retry: 2,
        staleTime: 1000 * 60 * 3 // 3 minutes
    });

    // -----------------------
    //    JOB PLAN LIST
    // -----------------------
    const { data: jobPlanList, isLoading: isJobPlanListLoading } = useQuery({
        queryKey: ['job', jobId, 'plan'],
        queryFn: async () => fetchPlanList(authDetail, { jobId: jobId! }),
        enabled: baseDefinedAndNotNullNarrowing(jobId)
    });

    // -----------------------
    //    JOB PRESTART LIST
    // -----------------------
    const { data: jobPrestartList, isLoading: isJobPrestartListLoading } = useQuery({
        queryKey: ['job', jobId, 'prestart'],
        queryFn: async () => fetchPrestartList(authDetail, { jobId: jobId! }),
        enabled: baseDefinedAndNotNullNarrowing(jobId)
    });

    // OLAP JOB HAZARD SUMMARY
    const { isLoading: isOlapJobHazardSummaryLoading, data: olapJobHazardSummary } = useQuery({
        queryKey: ['olapJobHazardSummary', jobId],
        queryFn: () => fetchOlapQuery<{ jobHazardSummary: 'true', jobId: string }>(authDetail, {
            jobHazardSummary: 'true',
            jobId: jobId!
        }),
        enabled: baseDefinedAndNotNullNarrowing(jobId)
    });

    const hasJobDetail = baseDefinedAndNotNullNarrowing(jobDetail);
    const {
        jobName, description
    } = jobDetail ??
        {
            jobName: undefined, description: undefined
        };
    const hasJobName = baseDefinedAndNotNullNarrowing(jobName);

    const hazardObservationMediaGroupsByHazardId = useMemo(() => {
        // ---------------------
        if (!baseDefinedAndNotNullNarrowing(olapJobHazardSummary)) return {} as Record<string, IHazardMedia[]>;
        return Object.groupBy(olapJobHazardSummary.hazardObsMedia, (hazardObservation) => hazardObservation.hazardId || 'noHazardId');
    }, [olapJobHazardSummary]);

    // MUTATIONS
    const patchPlanMutation = useMutation({
        mutationFn: patchPlan,
        onSuccess: (data) => {
            // ---------------------
            queueSnackbar(`The plan has been updated`, 'success', 'Success', 3000, '1rem');
            queryClient.invalidateQueries({ queryKey: ['plan', data.id] });
            queryClient.invalidateQueries({ queryKey: ['job', data.jobId, 'plan'] });
            queryClient.invalidateQueries({ queryKey: ['previousMonthPlanList'] });
            queryClient.invalidateQueries({ queryKey: ['currentMonthPlanList'] });
            queryClient.invalidateQueries({ queryKey: ['nextMonthPlanList'] });
        },
        onError: (error) => {
            // ----------------------------
            if (
                error instanceof AuthLibMSALTokenAcquisitionError
                || ['Unauthorized', 'Could not get fresh ID token'].includes(error.message)
            ) {
                // --------------------------------------------
                queueSnackbar(`We need to be sure it's really you. Redirecting you to login`, 'error', 'Unauthorised');
                const { msal } = authDetail;
                msal.instance.loginRedirect({
                    account: msal.accounts.length > 0 ? msal.accounts[0] : undefined,
                    scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"]
                });
            }
            else if (error instanceof Error) {
                logger.error('Error updating plan', { error });
            }
            logger.error('Error updating plan');
            queueSnackbar('Plan could not be updated', 'error', 'Error saving plan', 3000, '1rem');
        }
    });

    const patchPrestartMutation = useMutation({
        mutationFn: patchPrestart,
        onSuccess: (data) => {
            queueSnackbar(`The prestart has been updated`, 'success', 'Success', 3000, '1rem');
            queryClient.invalidateQueries({ queryKey: ['prestart', data.id] });
            queryClient.invalidateQueries({ queryKey: ['job', data.jobId, 'prestart'] });
            queryClient.invalidateQueries({ queryKey: ['previousMonthPrestartList'] });
            queryClient.invalidateQueries({ queryKey: ['currentMonthPrestartList'] });
            queryClient.invalidateQueries({ queryKey: ['nextMonthPrestartList'] });
        },
        onError: (error) => {
            // ----------------------------
            if (
                error instanceof AuthLibMSALTokenAcquisitionError
                || ['Unauthorized', 'Could not get fresh ID token'].includes(error.message)
            ) {
                // --------------------------------------------
                queueSnackbar(`We need to be sure it's really you. Redirecting you to login`, 'error', 'Unauthorised');
                const { msal } = authDetail;
                msal.instance.loginRedirect({
                    account: msal.accounts.length > 0 ? msal.accounts[0] : undefined,
                    scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"]
                });
            }
            else if (error instanceof Error) {
                logger.error('Error updating prestart', { error });
            }
            logger.error('Error updating prestart');
            queueSnackbar('Prestart could not be updated', 'error', 'Error saving prestart', 3000, '1rem');
        }
    });

    const loadFileUrlCb = useCallback((
        image: { filename: string } & IHazardMedia
    ) => loadFileUrl(image, authDetail, queueSnackbar), [authDetail, queueSnackbar]);

    const handlePrestartMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        // ---------------------
        setPrestartMenuAnchorEl(event.currentTarget);
    };
    const handlePrestartMenuClose = () => {
        // ---------------------
        setPrestartMenuAnchorEl(null);
    };

    const hasPlanList = baseDefinedAndNotNullNarrowing(jobPlanList) && jobPlanList.length > 0;

    return (
        <BasicAppBarPage bgColor={"white"}>
            <Container maxWidth="xl" sx={{ px: "25px" }}>
                <EmergencyDirectiveBanner />
                <Box display="flex" mb="10px">
                    <TextButton
                        color={theme.palette.primary.contrastText}
                        hoverBackgroundColor={theme.palette.primary.main}
                        onClick={() => {
                            navigate(`/`)
                        }}
                        startIcon={<ArrowBackIcon />}
                    >
                        {`Back to home`}
                    </TextButton>
                </Box>
                <Grid
                    container border="3px solid #e0e0e0"
                    columnSpacing={1.5} rowSpacing={1} alignItems="stretch"
                    mb="32px"
                >
                    <Grid
                        size={{ xs: 12 }}
                        py="10px"
                        height="auto"
                    >
                        <Box
                            px="20px"
                        >
                            <JobTitle
                                jobId={jobId}
                                jobName={hasJobName ? jobName : '...'}
                            />
                            <Box
                                display="flex" flexDirection="row"
                                pt="5px"
                            >
                                <Typography
                                    fontSize="0.8rem"
                                    color={theme.palette.primary.contrastText}
                                    fontWeight={300}
                                >
                                    {description}
                                </Typography>
                            </Box>
                            {
                                isJobDetailLoading &&
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    justifyContent="center"
                                >
                                    <BasicLoader
                                        size={50}
                                    />
                                </Box>
                            }
                            {
                                hasJobDetail &&
                                <Box
                                    display="flex" flexDirection="column"
                                >
                                    <GeneralChip
                                        label="Initiator"
                                        href={OrgSystem.constructSystemPath(INITIATOR, 'job', { jobId: jobId! })}
                                        size="vsmall"
                                        variant="outlined"
                                        backgroundColor={INITIATOR.bgColor}
                                        borderColor={INITIATOR.color}
                                        color={INITIATOR.color}
                                        clickable
                                        newTab={true}
                                    />
                                    <Box
                                        display="flex" flexDirection="row"
                                        pt="5px"
                                    >
                                        {
                                            SALESFORCE_JOB_STAFF_CONFIGURATION
                                                .map(role => (
                                                    baseDefinedAndNotNullNarrowing(jobDetail[role.key]) &&
                                                    <Box
                                                        key={`digital-job-detail-staff-avatar-${role.key}`}
                                                        px="2px"
                                                    >
                                                        <LabelledProfileImage
                                                            label={role.label}
                                                            avatarVariant='rounded'
                                                            avatarSize={isSmallerThanSm ? 35 : 50}
                                                            src={`${CDN_BASE_URL}/img/staff-media/${jobDetail[`${role.key}Id`]}.jpg`}
                                                            fontSize={isSmallerThanSm ? "0.5rem" : "0.6rem"}
                                                            color="white"
                                                            labelPosition='bottom'
                                                            labelMargin={isSmallerThanSm ? -2.5 : -3.2}
                                                            textShadow="1px 1px 2px black"
                                                        />
                                                    </Box>
                                                ))
                                        }
                                    </Box>
                                </Box>
                            }
                        </Box>
                    </Grid>
                    <Grid
                        size={{ xs: 12 }}
                        height="auto"
                    >
                        <Box
                            display="flex"
                            flexDirection="row"
                            justifyContent="flex-start"
                            px="20px"
                            py="10px"
                            gap="10px"
                        >
                            <FilledButton
                                variant="contained"
                                color={theme.palette.primary.contrastText}
                                backgroundColor={theme.palette.primary.main}
                                onClick={() => navigate(`/job/${jobId}/plan`)}
                            >
                                Create Plan
                            </FilledButton>
                            <FilledButton
                                variant="contained"
                                color={theme.palette.primary.contrastText}
                                backgroundColor={theme.palette.primary.main}
                                onClick={handlePrestartMenuClick}
                                disabled={!hasPlanList}
                            >
                                Create Prestart
                            </FilledButton>
                            <Menu
                                anchorEl={prestartMenuAnchorEl}
                                open={Boolean(prestartMenuAnchorEl)}
                                onClose={handlePrestartMenuClose}
                            >
                                <MenuItem
                                    disabled
                                    sx={{
                                        color: theme.palette.primary.contrastText,
                                        fontSize: isSmallerThanSm ? "0.75rem" : "0.8rem",
                                        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                                        "&.Mui-disabled": {
                                            color: theme.palette.text.primary
                                        }
                                    }}
                                >
                                    Choose a plan
                                </MenuItem>
                                {hasPlanList && jobPlanList
                                    .sort(comparePlanDates)
                                    .map((plan) => (
                                        <MenuItem
                                            key={`prestart-menu-item-${plan.id}`}
                                            sx={{
                                                color: theme.palette.primary.contrastText,
                                            }}
                                            onClick={() => {
                                                // ---------------------
                                                handlePrestartMenuClose();
                                                navigate(`/job/${jobId}/plan/${plan.id}/prestart`);
                                            }}
                                        >
                                            <Typography
                                                fontSize={isSmallerThanSm ? "0.8rem" : "0.9rem"}
                                            >
                                                {DateTime.fromISO(plan.startDate).toLocaleString(DateTime.DATE_MED)} - {baseDefinedAndNotNullNarrowing(plan.title) && plan.title.trim() !== '' ? plan.title : `Untitled Plan`}
                                            </Typography>
                                        </MenuItem>
                                    ))
                                }
                            </Menu>
                        </Box>
                    </Grid>
                    <Grid
                        size={{ xs: 12, md: 6, lg: 4 }}
                        height="auto"
                    >
                        <Box
                            display="flex" flexDirection="column"
                            px="20px"
                        >
                            <Typography
                                variant="body1" fontSize={isSmallerThanSm ? "0.9rem" : "1rem"}
                                color={theme.palette.primary.contrastText} fontWeight={700}
                            >
                                Hazard Dashboard
                            </Typography>
                            <Typography
                                fontSize={isSmallerThanSm ? "0.6rem" : "0.65rem"}
                                fontWeight={300}
                                color={theme.palette.primary.contrastText}
                            >
                                This board is focused on site specific hazards (expected and observed). Hazards from the register will also apply at this site.
                            </Typography>
                        </Box>
                        {
                            isOlapJobHazardSummaryLoading &&
                            <Box
                                display="flex"
                                flexDirection="row"
                                justifyContent="center"
                            >
                                <BasicLoader
                                    size={50}
                                />
                            </Box>
                        }
                        <Box
                            display="flex" flexDirection="column"
                            px="25px"
                            py="10px"
                        >
                            <Typography
                                fontSize={isSmallerThanSm ? "0.7rem" : "0.75rem"}
                                fontWeight={300}
                                color={theme.palette.primary.contrastText}
                            >
                                Critical or site specific hazards (from plans)
                            </Typography>
                            {
                                baseDefinedAndNotNullNarrowing(olapJobHazardSummary) &&
                                olapJobHazardSummary.criticalHazards.length === 0 &&
                                <Box
                                    display="flex" flexDirection="column"
                                    px="4px"
                                >
                                    <Typography
                                        fontSize={isSmallerThanSm ? "0.7rem" : "0.75rem"}
                                        fontWeight={300}
                                        color={amber[900]}
                                    >
                                        Warning - no critical or site specific hazards have been recorded for this job
                                    </Typography>
                                </Box>
                            }
                            {
                                baseDefinedAndNotNullNarrowing(olapJobHazardSummary) &&
                                olapJobHazardSummary.criticalHazards.length > 0 &&
                                <Box
                                    display="flex" flexDirection="column"
                                    rowGap="8px"
                                >
                                    {
                                        olapJobHazardSummary.criticalHazards
                                            .sort(compareHazardsByRiskLevel)
                                            .map((hazard, index) => (
                                                <CriticalOrSiteSpecificHazardListItemReadOnly
                                                    key={`hazard-observation-${index}`}
                                                    hazard={hazard}
                                                    actionButtons={[
                                                        <FilledButton
                                                            key={`view-plan-${hazard.planId}`}
                                                            size="vsmall"
                                                            tooltipTitle="View plan"
                                                            onClick={() => {
                                                                // ---------------------
                                                                navigate(`/job/${hazard.jobId}/edit-plan/${hazard.planId}?stage=hazardAssessment`);
                                                            }}
                                                        >
                                                            View in plan
                                                        </FilledButton>
                                                    ]}
                                                />
                                            ))
                                    }
                                </Box>
                            }
                        </Box>
                        <Box
                            display="flex" flexDirection="column"
                            px="25px"
                            pb="10px"
                        >
                            <Typography
                                fontSize={isSmallerThanSm ? "0.7rem" : "0.75rem"}
                                fontWeight={300}
                                color={theme.palette.primary.contrastText}
                            >
                                Hazard observations from site (from prestarts)
                            </Typography>
                            {
                                baseDefinedAndNotNullNarrowing(olapJobHazardSummary) &&
                                olapJobHazardSummary.hazardObservations.length === 0 &&
                                <Box
                                    display="flex" flexDirection="column"
                                    px="4px"
                                >
                                    <Typography
                                        fontSize={isSmallerThanSm ? "0.7rem" : "0.75rem"}
                                        fontWeight={300}
                                        color={amber[900]}
                                    >
                                        No hazard observations have been recorded in prestarts for this job
                                    </Typography>
                                </Box>
                            }
                            {
                                baseDefinedAndNotNullNarrowing(olapJobHazardSummary) &&
                                olapJobHazardSummary.hazardObservations.length > 0 &&
                                <Box
                                    display="flex" flexDirection="column"
                                    rowGap="8px"
                                >
                                    {
                                        olapJobHazardSummary.hazardObservations
                                            .filter(hazard => hazard.planStartDate !== null)
                                            .map((hazard, index) => (
                                                <PrestartHazardListItem
                                                    key={`hazard-observation-${index}`}
                                                    hazard={hazard}
                                                    actionButtons={[
                                                        <FilledButton
                                                            key={`view-prestart-${hazard.prestartId}`}
                                                            size="vsmall"
                                                            tooltipTitle="View prestart"
                                                            onClick={() => {
                                                                // ---------------------
                                                                navigate(`/job/${hazard.jobId}/plan/${hazard.planId}/view-prestart/${hazard.prestartId}`);
                                                                window.scrollTo(0, 0);
                                                            }}
                                                        >
                                                            View Prestart
                                                        </FilledButton>
                                                    ]}
                                                    images={baseDefinedAndNotNullNarrowing(hazard.id) ? (hazardObservationMediaGroupsByHazardId[hazard.id] ?? []).map(media => ({ ...media, filename: media.name })) : []}
                                                    loadImage={loadFileUrlCb}
                                                    showGalleryWhenCollapsed={false}
                                                />
                                            ))
                                    }
                                </Box>
                            }
                        </Box>
                    </Grid>
                    <Grid
                        size={{ xs: 12, md: 6, lg: 4 }}
                        height="auto"
                    >

                        <Box
                            display="flex" flexDirection="column"
                            px="20px"
                        >
                            <Typography
                                variant="body1" fontSize={isSmallerThanSm ? "0.9rem" : "1rem"}
                                color={theme.palette.primary.contrastText} fontWeight={700}
                            >
                                Plans
                            </Typography>
                        </Box>
                        {
                            isJobPlanListLoading &&
                            <Box
                                display="flex"
                                flexDirection="row"
                                justifyContent="center"
                            >
                                <BasicLoader
                                    size={50}
                                />
                            </Box>
                        }
                        {
                            baseDefinedAndNotNullNarrowing(jobPlanList) &&
                            jobPlanList.length === 0 &&
                            <Box
                                display="flex" flexDirection="column"
                                px="20px"
                                pt="10px"
                                pb="20px"
                            >
                                <Typography
                                    fontSize={isSmallerThanSm ? "0.8rem" : "0.9rem"}
                                    fontWeight={300}
                                    color={theme.palette.primary.contrastText}
                                >
                                    No plans have been recorded for this job
                                </Typography>
                            </Box>
                        }
                        {
                            baseDefinedAndNotNullNarrowing(jobPlanList) &&
                            jobPlanList.length > 0 &&
                            <Box
                                display="flex" flexDirection="column"
                                px="10px"
                                pb="20px"
                            >
                                {
                                    [...jobPlanList]
                                        .sort((a, b) => comparePlanDates(a, b, 'in_progress_upcoming_other_with_priority_subsort'))
                                        .map((plan) => {
                                            return (
                                                <PlanListItem
                                                    key={plan.id}
                                                    plan={plan}
                                                    isAdmin={isAdmin}
                                                    teamMemberList={fullTeamListData}
                                                    handleStatusChange={(planId, status, notify = false) => {
                                                        // ----------------------------------------
                                                        patchPlanMutation.mutateAsync({
                                                            authDetail,
                                                            planId,
                                                            patches: [
                                                                {
                                                                    "op": "replace",
                                                                    "path": "/status",
                                                                    "value": status
                                                                }
                                                            ],
                                                            notify
                                                        });
                                                    }}
                                                    handleEdit={() => navigate(`/job/${plan.jobId}/edit-plan/${plan.id}`)}
                                                    handleView={() => navigate(`/job/${plan.jobId}/view-plan/${plan.id}`)}
                                                    handleNewPrestart={() => navigate(`/job/${plan.jobId}/plan/${plan.id}/prestart`)}
                                                />
                                            )
                                        })
                                }
                            </Box>
                        }
                    </Grid>
                    <Grid
                        size={{ xs: 12, md: 6, lg: 4 }}
                        height="auto"
                    >
                        <Box
                            display="flex" flexDirection="column"
                            px="20px"
                        >
                            <Typography
                                variant="body1" fontSize={isSmallerThanSm ? "0.9rem" : "1rem"}
                                color={theme.palette.primary.contrastText} fontWeight={700}
                            >
                                Prestarts
                            </Typography>
                        </Box>
                        {
                            isJobPrestartListLoading &&
                            <Box
                                display="flex"
                                flexDirection="row"
                                justifyContent="center"
                            >
                                <BasicLoader
                                    size={50}
                                />
                            </Box>
                        }

                        {
                            baseDefinedAndNotNullNarrowing(jobPrestartList) &&
                            jobPrestartList.length === 0 &&
                            <Box
                                display="flex" flexDirection="column"
                                px="20px"
                                pt="10px"
                                pb="20px"
                            >
                                <Typography
                                    fontSize={isSmallerThanSm ? "0.8rem" : "0.9rem"}
                                    fontWeight={300}
                                    color={theme.palette.primary.contrastText}
                                >
                                    No prestarts have been recorded for this job
                                </Typography>
                            </Box>
                        }
                        {
                            baseDefinedAndNotNullNarrowing(jobPrestartList) &&
                            jobPrestartList.length > 0 &&
                            <Box
                                display="flex" flexDirection="column"
                                px="10px"
                                pb="20px"
                            >
                                {
                                    [...jobPrestartList]
                                        .sort(comparePrestartDates)
                                        .map((prestart) => {
                                            return (
                                                <PrestartListItem
                                                    key={`prestartlistitem-${prestart.id}`}
                                                    prestart={prestart}
                                                    isAdmin={isAdmin}
                                                    teamMemberList={fullTeamListData}
                                                    handleStatusChange={(prestartId, status, notify = false) => {
                                                        // ----------------------------------------
                                                        patchPrestartMutation.mutateAsync({
                                                            authDetail,
                                                            prestartId,
                                                            patches: [
                                                                {
                                                                    "op": "replace",
                                                                    "path": "/status",
                                                                    "value": status
                                                                }
                                                            ],
                                                            notify
                                                        });
                                                    }}
                                                    handleEdit={() => {
                                                        navigate(`/job/${prestart.jobId}/plan/${prestart.planId}/edit-prestart/${prestart.id}`);
                                                        window.scrollTo(0, 0);
                                                    }}
                                                    handleView={() => {
                                                        navigate(`/job/${prestart.jobId}/plan/${prestart.planId}/view-prestart/${prestart.id}`);
                                                        window.scrollTo(0, 0);
                                                    }}
                                                    handleViewPlan={() => {
                                                        navigate(`/job/${prestart.jobId}/view-plan/${prestart.planId}`);
                                                        window.scrollTo(0, 0);
                                                    }}
                                                />
                                            )
                                        })
                                }
                            </Box>
                        }
                    </Grid>
                </Grid>
                <BottomSearchAppBar
                    placeholder='Search for a job'
                    label='Search for a Job'
                    labelKey='jobId'
                    secondaryKeysForDisplay={['jobId']}
                    suggestionKeysForOptionDisplay={['jobId', 'jobName']}
                    suggestionKeysToSearchOption={['jobId', 'jobName']}
                    uniqueOptionKeyProps={['jobId']}
                    handleChange={(value) => { navigate(`/job/${value.jobId}`) }}
                    searchSuggestion={searchJob}
                />
            </Container>
        </BasicAppBarPage>
    )
}

export default JobDetail;